/**
 * Persisted multi-select for the customers list.
 *
 * Selection lives in the browser (localStorage), scoped per user + company,
 * so it survives filter changes and page reloads without any server state.
 * The server is only asked for totals over the selected ids (see
 * `services/customers.service.summary`).
 */
import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_PREFIX = "erp:customers:selection:v1";
const SELECTION_CAP = 5000;

function storageKeyFor(
  userId: string | null | undefined,
  companyId: string | null | undefined
): string | null {
  if (!userId || !companyId) return null;
  return `${STORAGE_PREFIX}:${userId}:${companyId}`;
}

function readIds(storageKey: string): string[] {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    // Corrupted JSON (or storage unavailable) — start clean rather than throw.
    return [];
  }
}

export interface UseCustomerSelectionResult {
  selectedIds: string[];
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  setMany: (ids: string[], checked: boolean) => void;
  remove: (ids: string[]) => void;
  clear: () => void;
  /** True once the selection has hit the 5000-id cap; further adds are ignored. */
  isAtLimit: boolean;
}

export function useCustomerSelection(
  userId: string | null | undefined,
  companyId: string | null | undefined
): UseCustomerSelectionResult {
  const storageKey = storageKeyFor(userId, companyId);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Load whenever the key changes (including the initial "both known" moment).
  useEffect(() => {
    setSelectedIds(storageKey ? readIds(storageKey) : []);
  }, [storageKey]);

  // Cross-tab sync: another tab writing the same key updates us too.
  useEffect(() => {
    if (!storageKey) return undefined;
    const onStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) return;
      setSelectedIds(event.newValue ? readIds(storageKey) : []);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey]);

  // Applies `updater` to the current selection and persists the result.
  // No-op while userId/companyId aren't known yet — there's nowhere durable
  // to put the change, and it would just be wiped by the load effect above
  // once the key becomes available.
  const update = useCallback(
    (updater: (prev: string[]) => string[]) => {
      if (!storageKey) return;
      setSelectedIds((prev) => {
        const next = updater(prev);
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // Private browsing / quota exceeded — keep the in-memory selection.
        }
        return next;
      });
    },
    [storageKey]
  );

  // Set lookup: the list calls this for every row several times per render
  // (row checkbox, header all/some state, "hidden" count) — with "show all"
  // that is thousands of rows against up to 5000 ids.
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const isSelected = useCallback(
    (id: string) => selectedSet.has(id),
    [selectedSet]
  );

  const toggle = useCallback(
    (id: string) => {
      update((prev) => {
        if (prev.includes(id)) return prev.filter((existing) => existing !== id);
        if (prev.length >= SELECTION_CAP) return prev;
        return [...prev, id];
      });
    },
    [update]
  );

  const setMany = useCallback(
    (ids: string[], checked: boolean) => {
      update((prev) => {
        if (!checked) {
          const toRemove = new Set(ids);
          return prev.filter((existing) => !toRemove.has(existing));
        }
        const existing = new Set(prev);
        const capacity = SELECTION_CAP - prev.length;
        if (capacity <= 0) return prev;
        const toAdd = ids.filter((id) => !existing.has(id)).slice(0, capacity);
        return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
      });
    },
    [update]
  );

  const remove = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      update((prev) => {
        const toRemove = new Set(ids);
        return prev.filter((existing) => !toRemove.has(existing));
      });
    },
    [update]
  );

  const clear = useCallback(() => {
    update(() => []);
  }, [update]);

  return {
    selectedIds,
    isSelected,
    toggle,
    setMany,
    remove,
    clear,
    isAtLimit: selectedIds.length >= SELECTION_CAP,
  };
}
